import dayjs from 'dayjs';
import { CalendarClock, ChevronDown, Info } from 'lucide-react';
import { Button, DatePicker, FilterResetButton, Dropdown, MenuItem, MenuLabel, Tooltip, cn } from '../../ui';
import { disableFromDate, disableToDate } from '../../lib/dateRange';
import { FilterField, type ReportDefinition, type ReportFilterDef } from './reportKit';
import { PRESETS, PRESET_GROUPS, presetFor } from './periodPresets';

/**
 * The parameters, on one band.
 *
 * Everything a report is run WITH, in the order it is decided: the period, then EVERY filter the
 * report declares, the most-used first. The band wraps onto a second line when a report has more
 * filters than one line holds.
 *
 * There used to be a "More filters" drawer holding the less-used ones. It hid filters behind a click
 * on a screen with a whole empty band of room beside the ones that were showing, so they are all
 * here now.
 */
export function ReportParameterBar({
  report, from, to, onRange, filters, values, onFilterChange, onReset, canReset, validRange,
}: {
  report: ReportDefinition;
  from: string;
  to: string;
  onRange: (from: string, to: string) => void;
  filters: ReportFilterDef[];
  /** Applied filter values (defaults folded in). */
  values: Record<string, any>;
  onFilterChange: (key: string, value: any) => void;
  onReset: () => void;
  canReset: boolean;
  validRange: boolean;
}) {
  const activePreset = presetFor(from, to);
  const days = dayjs(to).diff(dayjs(from), 'day') + 1;

  return (
    <div className="flex flex-wrap items-end gap-x-2 gap-y-2 border-b border-line py-2">
      {report.pointInTime ? (
        /* A snapshot report says so rather than offering a picker that changes nothing. */
        <Field label="Period">
          <span className="inline-flex h-8 items-center gap-1.5 rounded-control border border-line bg-surface-2 px-2.5 text-sm text-muted">
            <CalendarClock className="size-3.5 text-subtle" />
            Snapshot — as of today
          </span>
        </Field>
      ) : (
        <>
          {/* The day count rides on the LABEL. As a separate span between the controls it had no
              column of its own, so at some widths it sat underneath the next filter. */}
          <Field label={validRange ? `Period · ${days} day${days === 1 ? '' : 's'}` : 'Period'} hint={report.periodMeans}>
            <Dropdown
              align="start"
              panelClassName="min-w-[252px]"
              panel={
                <div>
                  {PRESET_GROUPS.map((g) => (
                    <div key={g}>
                      <MenuLabel>{g}</MenuLabel>
                      {PRESETS.filter((p) => p.group === g).map((p) => {
                        const [pf, pt] = p.range();
                        return (
                          <MenuItem
                            key={p.label}
                            active={pf === from && pt === to}
                            onClick={() => onRange(pf, pt)}
                            trailing={<span className="type-caption text-subtle">{dayjs(pf).format('DD MMM')} – {dayjs(pt).format('DD MMM')}</span>}
                          >
                            {p.label}
                          </MenuItem>
                        );
                      })}
                    </div>
                  ))}
                </div>
              }
            >
              <Button variant="default" size="sm" icon={<CalendarClock size={14} />} className="min-w-[132px] justify-between">
                {activePreset ?? 'Custom range'}
                <ChevronDown className="ml-1 size-3.5 text-subtle" />
              </Button>
            </Dropdown>
          </Field>

          {/* 168px, not less: the date field's own minimum is 9rem plus its icon, its value and its
              clear button, and a narrower box let that clear button hang outside the control — where
              it read as a stray ✕ belonging to the filter beside it. */}
          <Field label="From">
            <DatePicker
              size="small"
              className="w-[168px]"
              value={from ? dayjs(from) : null}
              onChange={(dt) => onRange(dt?.format('YYYY-MM-DD') ?? '', to)}
              placeholder="From"
              disabledDate={disableFromDate(to)}
            />
          </Field>
          <Field label="To">
            <DatePicker
              size="small"
              className="w-[168px]"
              value={to ? dayjs(to) : null}
              onChange={(dt) => onRange(from, dt?.format('YYYY-MM-DD') ?? '')}
              placeholder="To"
              disabledDate={disableToDate(from)}
            />
          </Field>
          {!validRange && (
            <span className="inline-flex h-8 items-center type-caption text-danger-600 dark:text-danger-500">
              “From” is after “To”
            </span>
          )}
        </>
      )}

      {/* A thin rule between the period and the report's own filters — the two are different kinds of
          parameter, and a reader should not have to work out where one ends. */}
      {!!filters.length && <span className="mx-1 hidden h-8 w-px self-end bg-line lg:block" aria-hidden />}

      {/* Narrower at 1366 so more of the band fits one line, roomier once there is space. */}
      {filters.map((f) => (
        <div key={f.key} className="w-[164px] min-w-0 xl:w-[190px]">
          <FilterField
            compact
            filter={f}
            value={values[f.key]}
            values={values}
            onChange={(v) => onFilterChange(f.key, v)}
          />
        </div>
      ))}

      {/* Right after the last filter, not pushed to the far edge — it acts on the filters, so it sits
          beside them instead of across a gap the width of the screen. */}
      {canReset && (
        <div className="flex items-end">
          <FilterResetButton onClick={onReset} />
        </div>
      )}
    </div>
  );
}

/** One labelled control on the bar: a 10px label over a 32px control, and nothing else. */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-0.5">
      <span className="flex items-center gap-1 type-label text-subtle">
        {label}
        {hint && (
          <Tooltip title={hint}>
            <Info className={cn('size-3 shrink-0 cursor-help text-subtle')} />
          </Tooltip>
        )}
      </span>
      {children}
    </label>
  );
}
